import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import {
  Search,
  Filter,
  PlusSquare,
  Edit,
  Trash2,
  StickyNoteIcon,
  Link as LinkIcon,
  Layers,
  XCircle,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { usePages } from "../../hooks/usePages";
import { useContents } from "../../hooks/useContents";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import PageForm from "./PageForm";
import { toast } from "react-toastify";
import { Link, useResolvedPath } from "react-router-dom";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import Breadcrumb from "../../components/ui/Breadcrumb";

function PageManagement() {
  const { logout } = useAuth();
  const [pagesList, setPagesList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPage, setSelectedPage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view, edit, create
  const [contentsList, setContentsList] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: 1,
    showing: 10,
    total: 0,
  });

  const queryClient = useQueryClient();

  // Assume usePages returns CRUD functions similar to useUsers
  const { fetchPages, createPage, deletePage, updatePage } = usePages({
    pagination: { page: pageData.currentPage, limit: 10 },
    sort: { field: "created_at", direction: "desc" },
  });

  const {
    fetchContents, // fetches an array of content items: { _id, title }
  } = useContents();

  const pagesQuery = useQuery({
    queryKey: ["pages", pageData.currentPage],
    queryFn: fetchPages,
    keepPreviousData: true,
  });

  const contentsQuery = useQuery({
    queryKey: ["contents", selectedPage?._id],
    queryFn: () => {
      if (!selectedPage?._id) return Promise.resolve({ data: [] });
      else return fetchContents(selectedPage._id);
    },
    enabled: !!selectedPage?._id,
  });

  useEffect(() => {
    if (pagesQuery.data) {
      const response = pagesQuery.data;
      setPagesList(response?.data?.cms || []);
      setPageData({
        currentPage: response?.data?.pagination?.page || 1,
        totalPages: response?.data?.pagination?.lastPage || 1,
        total: response?.data?.pagination?.total || 0,
        showing: response?.data?.pagination?.showing || 10,
      });
    }
  }, [pagesQuery.data]);

  useEffect(() => {
    if (!contentsQuery.isLoading) {
      setContentsList(contentsQuery?.data?.data || []);
    }
  }, [contentsQuery.data, contentsQuery.isLoading]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const openModal = (page, mode) => {
    setSelectedPage(page);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPage(null);
  };

  const handlePageChange = (page) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: page,
      showing: 10,
    }));
  };

  const filteredPages = pagesList.filter((page) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        page.title.toLowerCase().includes(query) ||
        page.path.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleUpdate = (page) => {
    openModal(page, "edit");
  };

  const handleDelete = {
    onDelete: (page) =>
      mutation.deletePage.mutate(page._id, {
        onSuccess: (data) => {
          toast.success(data.message || "Page Deleted");
          setDeleteModal(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to delete page");
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (page) => {
      setDeleteModal(true);
      setSelectedPage(page);
    },
  };

  const mutation = {
    createPage: useMutation({
      mutationFn: (body) => {
        if (modalMode === "edit") {
          const { _id, ...rest } = body;
          return updatePage(_id, rest);
        }
        return createPage(body);
      },
    }),
    deletePage: useMutation({
      mutationFn: (id) => deletePage(id),
    }),
  };

  const handleSubmit = (body) => {
    if (modalMode === "edit") {
      body._id = selectedPage._id;
    }

    mutation.createPage.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["pages"]);
        toast.success(
          data?.data?.message ||
            `Page ${modalMode === "edit" ? "Updated" : "Created"} successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save page");
      },
    });
  };

  const breadcrumbItems = [
    { label: "CMS", to: "/cms" },
  ];

  return (
    <div>
      <main className="">
        <Breadcrumb items={breadcrumbItems} />
        {/* Header and Add Button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Page Management</h1>
          {/* <button
            onClick={() => openModal({}, "create")}
            className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            <PlusSquare className="mr-2 h-4 w-4" />
            Add New Page
          </button> */}
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Table or Skeleton */}
          {pagesQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Title
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Path
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Contents
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPages.length > 0 ? (
                    filteredPages.map((page) => (
                      <tr key={page._id} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <Layers className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">
                                <Link to={`/cms/contents/${page._id}`}>
                                  {page.title}
                                </Link>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex items-center">
                            <LinkIcon className="mr-1 h-4 w-4 text-gray-400" />
                            <span>{page.path}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {Array.isArray(page.contents) &&
                          page.contents.length > 0
                            ? page.contents
                                .map((cid) => {
                                  return cid ? cid.title : "-";
                                })
                                .join(", ")
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(page.created_at).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openModal(page, "view")}
                              className="text-gray-400 hover:text-gray-500"
                              title="View Details"
                            >
                              <StickyNoteIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUpdate(page)}
                              className="text-blue-400 hover:text-blue-500"
                              title="Edit Page"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              className="text-red-400 hover:text-red-500"
                              onClick={() => handleDelete.onOpen(page)}
                              title="Delete Page"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No pages found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handlePageChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedPage)}
        title="Page"
      />
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Page Details"
                  : modalMode === "edit"
                  ? "Edit Page"
                  : "Create Page"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <PageForm
                mode={modalMode}
                defaultValues={
                  selectedPage
                    ? {
                        ...selectedPage,
                        created_at: moment(selectedPage.created_at).format(
                          "YYYY-MM-DD"
                        ),
                        contentIds: selectedPage.contentIds || [],
                      }
                    : {}
                }
                onCancel={closeModal}
                contentsList={selectedPage?.contents || []}
                onSubmit={(data) => handleSubmit(data)}
                submissionLoading={mutation.createPage.isLoading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PageManagement;
